import type { AudioAssetRef, Project } from '@/domain/types';
import type { WaveformPeaks } from '@/assets/waveformTypes';

/**
 * The storage seam.
 *
 * Everything above this interface — the DAW domain, the audio engine, the UI —
 * is unaware of *where* projects live. v1 ships `IndexedDBStorage`, which keeps
 * everything on the user's machine. A future `CloudStorageProvider` can satisfy
 * the same contract without the DAW changing, which is the whole point: local
 * first, cloud optional, never cloud-required.
 *
 * Implementations must be safe to call concurrently and must never throw for
 * "not found" — they return null instead. Genuine failures (quota, corruption,
 * permission) throw `StorageError`.
 */

export interface ProjectSummary {
  id: string;
  name: string;
  createdAt: number;
  modifiedAt: number;
  /** Approximate serialised size of the project document, in bytes. */
  sizeBytes: number;
  trackCount: number;
  clipCount: number;
}

export interface StoredAsset {
  meta: AudioAssetRef;
  /** Encoded source bytes exactly as imported or recorded. */
  blob: Blob;
}

export interface RecoverySnapshot {
  projectId: string;
  projectName: string;
  savedAt: number;
  project: Project;
  /** True once the user has explicitly saved since this snapshot was taken. */
  superseded: boolean;
}

/**
 * A cached render: decoded samples, not encoded bytes.
 *
 * Source audio is stored encoded, because decoding it again is cheap. A render
 * is the opposite case — reproducing one costs a second or more of WSOLA — so it
 * is worth keeping the samples themselves, and worth keeping them at full
 * precision: a render is an intermediate that is then summed with others, and
 * quantising it would put noise into the mix rather than into a preview.
 */
export interface StoredRender {
  channels: Float32Array[];
  sampleRate: number;
}

/** What is known about a cached render without reading its samples back. */
export interface RenderSummary {
  key: string;
  /** The asset the render was made from, so it can be dropped with that asset. */
  assetId: string;
  bytes: number;
  lastUsedAt: number;
}

export interface StorageUsage {
  usageBytes: number | null;
  quotaBytes: number | null;
  persisted: boolean;
  /** How much of the usage is cached renders, and how much they are allowed. */
  renderBytes: number;
  renderBudgetBytes: number;
}

export type StorageErrorCode =
  | 'unavailable'
  | 'quota'
  | 'corrupt'
  | 'blocked'
  | 'notFound'
  | 'unknown';

export class StorageError extends Error {
  constructor(
    readonly code: StorageErrorCode,
    message: string,
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'StorageError';
  }
}

export interface StorageProvider {
  readonly id: string;

  init(): Promise<void>;

  listProjects(): Promise<ProjectSummary[]>;
  loadProject(projectId: string): Promise<Project | null>;
  saveProject(project: Project): Promise<void>;
  deleteProject(projectId: string): Promise<void>;

  putAsset(meta: AudioAssetRef, blob: Blob): Promise<void>;
  getAsset(assetId: string): Promise<StoredAsset | null>;
  hasAsset(assetId: string): Promise<boolean>;
  deleteAsset(assetId: string): Promise<void>;
  listAssetIds(): Promise<string[]>;

  putWaveform(assetId: string, peaks: WaveformPeaks): Promise<void>;
  getWaveform(assetId: string): Promise<WaveformPeaks | null>;

  /**
   * The render cache, behind the same seam as everything else.
   *
   * `key` is the engine's own content key for the render — asset, source
   * transforms and length ratio — so an implementation never has to know what a
   * stretch or a transposition is. It is only obliged to give back what it was
   * given, or null.
   *
   * Storing a render is an optimisation and is allowed to decline: an
   * implementation that is out of space, or that considers one render too large
   * to be worth a slot, returns normally without storing it. Callers must treat
   * a later miss as ordinary.
   */
  putRender(key: string, assetId: string, render: StoredRender): Promise<void>;
  getRender(key: string): Promise<StoredRender | null>;
  listRenders(): Promise<RenderSummary[]>;
  deleteRender(key: string): Promise<void>;

  saveRecovery(snapshot: RecoverySnapshot): Promise<void>;
  loadRecovery(): Promise<RecoverySnapshot | null>;
  clearRecovery(): Promise<void>;

  getSetting<T>(key: string): Promise<T | null>;
  setSetting<T>(key: string, value: T): Promise<void>;

  /**
   * Deletes assets and waveforms no longer referenced by any stored project,
   * and the cached renders that were made from them.
   */
  collectGarbage(): Promise<{ removedAssets: number; removedRenders: number; freedBytes: number }>;

  estimateUsage(): Promise<StorageUsage>;
}
